/**
 * App Layout
 * Main layout with bottom navigation
 */

import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { Package, Clock, User, LogOut } from 'lucide-react'
import { useAppStore } from '../../stores'
import { cn } from '../../utils/helpers'

// =============================================================================
// NAV ITEMS
// =============================================================================

const navItems = [
  { to: '/clock', icon: Clock, label: 'Clock' },
  { to: '/delivery', icon: Package, label: 'Delivery' },
]

// =============================================================================
// COMPONENT
// =============================================================================

export function AppLayout() {
  const { user, logout } = useAppStore()
  const navigate = useNavigate()
  
  const handleLogout = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to logout?')
    if (!confirmed) {
      return
    }

    try {
      // Clear browser storage
      localStorage.clear()
      sessionStorage.clear()
      
      // Clear cookies
      document.cookie.split(";").forEach((c) => {
        const eqPos = c.indexOf("=")
        const name = eqPos > -1 ? c.substr(0, eqPos) : c
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/"
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname
      })
      
      // Clear cache if available
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        )
      }
      
      // Logout from store
      logout()
      
      // Navigate to login
      navigate('/login')
      
      // Force page reload to ensure clean state
      window.location.reload()
    } catch (error) {
      console.error('Error during logout:', error)
      // Fallback: still logout even if clearing fails
      logout()
      navigate('/login')
    }
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
              <Package className="w-4 h-4 text-white" />
            </div>
            <span className="font-medium text-gray-900">SPT Driver</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        <Outlet />
      </main>
      
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="max-w-lg mx-auto">
          <div className="flex">
            {navItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex-1 flex flex-col items-center justify-center py-3 transition-colors',
                    isActive
                      ? 'text-black'
                      : 'text-gray-400 hover:text-gray-600'
                  )
                }
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs mt-1">{label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
    </div>
  )
}
